import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, FileText, Info, Save } from 'lucide-react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  PageHeader,
  Select,
  Skeleton,
  StatusPill,
  invoiceStatusToPill,
  toast,
  useReturnOrigin,
  withReturn,
} from '../../components/ui';
import { formatMoney } from '../../lib/format';
import {
  useInvoice,
  useInvoiceClients,
  useUpdateInvoice,
  type InvoiceDetail,
} from './api';
import {
  LineItemsEditor,
  MetaFields,
  lineAmount,
  linesSubtotal,
  metaFromInvoice,
  metaDiscount,
  metaTax,
  newLineKey,
  suggestSubject,
  type EditableLine,
  type InvoiceMeta,
  kindOptionsFor,
} from './line-editor';
import { invoiceListHref } from './list-view';

const INVOICE_KIND_OPTIONS = [
  { value: 'service', label: 'Service' },
  { value: 'support', label: 'Support' },
  { value: 'recurring', label: 'Recurring' },
  { value: 'other', label: 'Other' },
];

function linesFromInvoice(inv: InvoiceDetail): EditableLine[] {
  return inv.lineItems.map((li) => ({
    key: newLineKey(),
    description: li.description,
    agreementId: li.agreement?.id ?? null,
    quantity: String(li.quantity),
    unitPrice: li.unitPrice.toFixed(2),
    kind: li.kind,
    timeEntryIds: [],
    expenseIds: [],
    source: 'manual' as const,
  }));
}

// ---------------------------------------------------------------------------
// Editor (mounted only once the draft is loaded — state seeds from props)
// ---------------------------------------------------------------------------

function DraftEditor({ invoice }: { invoice: InvoiceDetail }) {
  // Sent but not settled: correctable, same number, same client (the server's
  // rule) — and the client still holds the old version until it goes again.
  const sent = invoice.state !== 'DRAFT';
  const navigate = useNavigate();
  const from = useReturnOrigin();
  const clients = useInvoiceClients();
  const update = useUpdateInvoice();
  const [error, setError] = useState<string | null>(null);

  const [clientId, setClientId] = useState(invoice.client.id);
  const [kind, setKind] = useState(invoice.kind);
  const [lines, setLines] = useState<EditableLine[]>(() => linesFromInvoice(invoice));
  const [meta, setMeta] = useState<InvoiceMeta>(() => metaFromInvoice(invoice));
  const [badKeys, setBadKeys] = useState<Set<string>>(() => new Set());

  const hasLinked = invoice.linkedTimeEntryCount > 0 || invoice.linkedExpenseCount > 0;

  /**
   * ⚠ AN ERROR BELONGS TO THE ROW THAT PRODUCED IT.
   *
   * "Line amounts cannot be negative." used to print at the bottom-left of the
   * card while Save sat bottom-right, and nothing on screen said WHICH line —
   * on a twelve-line invoice that is a hunt through the grid. `fail` marks the
   * offending rows, scrolls the first into view and names the line number.
   */
  const fail = (message: string, keys: string[]) => {
    setError(message);
    setBadKeys(new Set(keys));
    if (keys.length > 0) {
      window.requestAnimationFrame(() => {
        const row = document.querySelector<HTMLElement>(`[data-line-key="${keys[0]}"]`);
        row?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        row?.querySelector<HTMLElement>('textarea,input')?.focus();
      });
    }
  };

  const save = () => {
    const usable = lines.filter((l) => l.description.trim());
    if (usable.length === 0) {
      fail('An invoice needs at least one line item with a description.', [lines[0]?.key].filter(Boolean) as string[]);
      return;
    }
    const noQty = usable.filter((l) => !Number.isFinite(Number(l.quantity)) || Number(l.quantity) <= 0);
    if (noQty.length > 0) {
      fail(
        `Line ${lines.indexOf(noQty[0]) + 1} needs a positive quantity.`,
        noQty.map((l) => l.key),
      );
      return;
    }
    const negative = usable.filter((l) => lineAmount(l) < 0);
    if (negative.length > 0) {
      fail(
        `Line ${lines.indexOf(negative[0]) + 1} has a negative amount — an invoice line cannot be a credit.`,
        negative.map((l) => l.key),
      );
      return;
    }
    setError(null);
    setBadKeys(new Set());
    update.mutate(
      {
        id: invoice.id,
        ...(sent ? {} : { clientId }),
        kind,
        issueDate: meta.issueDate,
        dueDate: meta.dueDate,
        notes: meta.notes.trim() || null,
        // Same rule as the wizard: the placeholder is the default the form
        // saves, not a different string the list will invent later.
        subject: meta.subject.trim() || suggestSubject(usable, meta.issueDate),
        poNumber: meta.poNumber.trim() || null,
        discountAmount: metaDiscount(meta, linesSubtotal(usable)),
        taxAmount: metaTax(
          meta,
          linesSubtotal(usable) - metaDiscount(meta, linesSubtotal(usable)),
        ),
        lines: usable.map((l) => ({
          description: l.description.trim(),
          agreementId: l.agreementId,
          quantity: Number(l.quantity),
          unitPrice: Number(l.unitPrice) || 0,
          kind: l.kind,
        })),
      },
      {
        onSuccess: (saved) => {
          toast.success(
            sent
              ? `Invoice #${saved.number} updated — ${formatMoney(saved.amount)}. Use Re-send invoice so the client has this version.`
              : `Draft #${saved.number} saved — ${formatMoney(saved.amount)}`,
          );
          navigate(withReturn(`/invoices/${invoice.id}`, from));
        },
        onError: (e) => setError(e instanceof Error ? e.message : 'Could not save the invoice'),
      },
    );
  };

  return (
    <Card>
      <CardHeader
        title="Invoice details"
        subtitle={
          sent
            ? "Saving replaces this invoice's line items with what you see here."
            : "Saving replaces the draft's line items with what you see here."
        }
      />
      <CardBody>
        {sent && (
          <p className="mb-4 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2.5 text-body-sm text-secondary">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
            <span>
              Invoice #{invoice.number} has already been sent. Your changes keep its number, but the client
              still has the old version — after saving, use <strong>Re-send invoice</strong> on the invoice to
              email them the updated one.
              {invoice.paidAmount > 0 &&
                ` ${formatMoney(invoice.paidAmount)} has been paid, so the total cannot go below that.`}
            </span>
          </p>
        )}
        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:max-w-2xl">
          <Select
            label="Client"
            value={clientId}
            disabled={sent}
            title={sent ? 'A sent invoice stays with its client — duplicate it to bill someone else.' : undefined}
            onChange={(e) => setClientId(e.target.value)}
            options={(clients.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
          />
          <Select
            label="Invoice kind"
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            options={kindOptionsFor(invoice.kind, INVOICE_KIND_OPTIONS)}
          />
        </div>
        <MetaFields meta={meta} onChange={setMeta} subtotal={linesSubtotal(lines)} lines={lines} />
        {hasLinked && (
          <p className="mt-4 flex items-start gap-2 rounded-lg bg-accent-50 px-3 py-2.5 text-body-sm text-accent-700">
            <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>
              This {sent ? 'invoice' : 'draft'} bills {invoice.linkedTimeEntryCount} time entr
              {invoice.linkedTimeEntryCount === 1 ? 'y' : 'ies'}
              {invoice.linkedExpenseCount > 0 &&
                ` and ${invoice.linkedExpenseCount} expense${invoice.linkedExpenseCount === 1 ? '' : 's'}`}
              . They stay locked to it while you edit —{' '}
              {sent ? 'voiding the invoice releases them.' : 'deleting or voiding the draft releases them.'}
            </span>
          </p>
        )}
      </CardBody>
      <div className="border-t border-grey-200">
        <LineItemsEditor
          lines={lines}
          onChange={(next) => {
            setLines(next);
            if (badKeys.size > 0) setBadKeys(new Set());
          }}
          allowAdd
          meta={meta}
          invalidKeys={badKeys}
        />
      </div>
      <CardBody className="border-t border-grey-200 pt-4">
        <div className="flex flex-wrap items-center justify-end gap-3">
          {/* Beside the button that raised it, never under the far corner. */}
          {error && (
            <p
              role="alert"
              className="mr-auto flex items-center gap-1.5 text-body-sm font-semibold text-error"
            >
              <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
              {error}
            </p>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(withReturn(`/invoices/${invoice.id}`, from))}>
              Cancel
            </Button>
            <Button size="lg" loading={update.isPending} onClick={save}>
              <Save className="h-4 w-4" aria-hidden />
              {sent ? 'Save changes' : 'Save draft'}
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function EditInvoicePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const invoice = useInvoice(id);
  // Carried through from the invoice, so editing a draft opened from a client
  // does not quietly drop the reader out of that client on the way back.
  const from = useReturnOrigin();

  if (invoice.isLoading) {
    return (
      <div className="mx-auto w-full max-w-5xl">
        <Skeleton className="mb-2 h-5 w-32" />
        <Skeleton className="mb-6 h-9 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (invoice.isError || !invoice.data) {
    return (
      <div className="mx-auto w-full max-w-5xl">
        <Card>
          <EmptyState
            icon={<FileText />}
            title="Invoice not found"
            description={
              invoice.error instanceof Error ? invoice.error.message : 'It may have been deleted.'
            }
            action={
              <Button variant="outline" onClick={() => navigate(invoiceListHref())}>
                <ArrowLeft className="h-4 w-4" aria-hidden />
                Back to invoices
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  const inv = invoice.data;

  if (inv.state === 'PAID' || inv.state === 'VOID') {
    return (
      <div className="mx-auto w-full max-w-5xl">
        <Card>
          <EmptyState
            icon={<FileText />}
            title={inv.state === 'PAID' ? 'A paid invoice cannot be changed' : 'A voided invoice cannot be changed'}
            description={`Invoice #${inv.number} is ${inv.state.toLowerCase()} — its line items are locked. Duplicate it from the invoice page to start a new one from it.`}
            action={
              <Button variant="outline" onClick={() => navigate(withReturn(`/invoices/${inv.id}`, from))}>
                <ArrowLeft className="h-4 w-4" aria-hidden />
                View invoice
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-5xl">
      <Link
        to={withReturn(`/invoices/${inv.id}`, from)}
        className="mb-3 inline-flex items-center gap-1.5 text-body-sm font-semibold text-grey-500 transition-colors hover:text-secondary"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Invoice #{inv.number}
      </Link>
      <PageHeader
        title={inv.state === 'DRAFT' ? `Edit draft #${inv.number}` : `Edit invoice #${inv.number}`}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <StatusPill status={invoiceStatusToPill(inv.state, inv.isLate)} />
            <span>{inv.client.name}</span>
          </span>
        }
      />
      <DraftEditor key={inv.id + inv.updatedAt} invoice={inv} />
    </div>
  );
}
